/**
 * One warning surface for the employee, day and shift badges (issue #146, design in
 * docs/design/warning-popovers/): categories, the list, then one issue's evidence and the way on.
 * Presentation only: it draws the issues it is given (warningPopover.types.ts) and reports which
 * one was acted on; the page decides where that leads. Anchored beside the badge on a wide screen
 * (Radix Popover: collision, flip, Escape, outside click, focus back to the badge); a modal bottom
 * sheet on a phone (Radix Dialog: inert background, focus kept inside). Reading changes nothing.
 * Styles are local (WarningPopover.module.css).
 */
import { T } from '@/i18n';
import { Icon } from '@/ui/icons';
import * as Dialog from '@radix-ui/react-dialog';
import * as Popover from '@radix-ui/react-popover';
import { type ReactElement, useLayoutEffect, useRef, useState } from 'react';
import css from './WarningPopover.module.css';
import type { Cat, Issue } from './warningPopover.types';
import { CATS } from './warningPopoverAdapter';

/**
 * When the warnings open as the bottom sheet: a phone, or a screen too short for an anchored list
 * (a laptop at 200% zoom is about 640×400; anchored under its badge the list had about 10px, WP-12).
 */
export const SHEET_QUERY = '(max-width:560px), (max-height:520px)';

export interface WarningPopoverProps {
  /** the badge; it becomes the trigger */
  trigger: ReactElement;
  /** person, day, or person and interval */
  title: string;
  /** "Uke 42", "Hele dagen", "Torsdag 15.10" */
  scope: string;
  issues: Issue[];
  /** a bottom sheet instead of the anchored popover */
  sheet: boolean;
  /** still reading, or the read failed; ready when left out */
  status?: 'loading' | 'error' | undefined;
  onRetry?: (() => void) | undefined;
  /** the issue's button was pressed; the popover has closed */
  onAction: (issue: Issue) => void;
  /** a setup link was pressed: the page navigates, through its own unsaved-change blocker */
  onSetup?: ((to: string) => void) | undefined;
  /** Gjennomgå: exactly the cases shown, and the category they were filtered by */
  onReview: (keys: string[], cat: Cat | null) => void;
  /** open from the start (the fixtures) */
  defaultOpen?: boolean;
  /** told when it opens and closes: the page keeps a badge whose last case resolved while open */
  onOpenChange?: ((open: boolean) => void) | undefined;
}

export function WarningPopover(p: WarningPopoverProps) {
  const [open, setOpenState] = useState(!!p.defaultOpen);
  const setOpen = (o: boolean) => {
    setOpenState(o);
    p.onOpenChange?.(o);
  };
  const body = <Body {...p} close={() => setOpen(false)} />;
  if (p.sheet)
    return (
      <Dialog.Root open={open} onOpenChange={setOpen}>
        <Dialog.Trigger asChild>{p.trigger}</Dialog.Trigger>
        <Dialog.Portal>
          <Dialog.Overlay className="scrim" />
          <Dialog.Content
            className={`${css.pop} ${css.sheet}`}
            aria-describedby={undefined}
            // Radix hides the page behind it and keeps focus inside; this says so to assistive
            // technology as well (follow-up review: aria-modal was absent)
            aria-modal="true"
            data-testid="warn-pop"
          >
            {body}
          </Dialog.Content>
        </Dialog.Portal>
      </Dialog.Root>
    );
  return (
    <Popover.Root open={open} onOpenChange={setOpen}>
      <Popover.Trigger asChild>{p.trigger}</Popover.Trigger>
      <Popover.Portal>
        <Popover.Content
          className={css.pop}
          aria-label={`${p.title}, ${p.scope}`}
          sideOffset={12}
          collisionPadding={{ left: 12, right: 12, top: 16, bottom: 16 }}
          data-testid="warn-pop"
        >
          {body}
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  );
}

const count = (n: number) => (n === 1 ? T('1 varsel') : T('{n} varsler', { n }));
const SevIcon = ({ sev }: { sev: 'd' | 'w' }) => (
  <i className={css.icon} data-sev={sev}>
    <Icon name={sev === 'd' ? 'stop' : 'warn'} size={18} strokeWidth={1.7} />
  </i>
);

/** The inside; it mounts with each opening, so a new opening starts at the list under Alle. */
function Body({
  title,
  scope,
  issues,
  sheet,
  status,
  onRetry,
  onAction,
  onReview,
  onSetup,
  close,
}: WarningPopoverProps & { close: () => void }) {
  const [cat, setCat] = useState<Cat | null>(null);
  const [openKey, setOpenKey] = useState<string | null>(null);
  const back = useRef<string | null>(null);
  const listRef = useRef<HTMLDivElement | null>(null);
  const scrollTop = useRef(0);
  // in the order the list first shows them, as the reference does
  const cats = [...new Set(issues.map((x) => x.cat))].map(
    (c) => CATS.find(([k]) => k === c) as [Cat, string],
  );
  const shown = cat ? issues.filter((x) => x.cat === cat) : issues;
  const current = openKey ? issues.find((x) => x.key === openKey) : undefined;
  // back from an issue: the list where it was, and focus on the row that opened it (WP-06)
  useLayoutEffect(() => {
    if (openKey || !back.current) return;
    if (listRef.current) listRef.current.scrollTop = scrollTop.current;
    listRef.current
      ?.querySelector<HTMLElement>(`[data-key="${back.current}"]`)
      ?.focus({ preventScroll: true });
    back.current = null;
  }, [openKey]);
  const Title = sheet ? Dialog.Title : 'h2';
  const Close = sheet ? Dialog.Close : Popover.Close;
  const catName = cats.find(([c]) => c === cat)?.[1];
  return (
    <>
      <div className={css.head} data-testid="warn-head">
        <div className={css.headText}>
          <Title className={css.title}>{title}</Title>
          <p className={css.scope} aria-live="polite">
            {scope}
            {status ? '' : ` · ${count(issues.length)}`}
          </p>
        </div>
        <Close asChild>
          <button type="button" className={css.close} aria-label={T('Lukk')} data-testid="warn-close">
            <Icon name="close" size={18} />
          </button>
        </Close>
      </div>
      {status === 'loading' ? (
        <div className={css.list}>
          <p className={css.none} aria-busy="true">
            {T('Henter varsler …')}
          </p>
        </div>
      ) : status === 'error' ? (
        <div className={css.list}>
          {/* a failed read is not "no warnings" (WP-16) */}
          <p className={css.none}>
            {T('Kunne ikke hente varslene. Det betyr ikke at det ikke finnes noen.')}
          </p>
          {onRetry && (
            <button type="button" className={css.link} onClick={onRetry}>
              {T('Prøv igjen')}
            </button>
          )}
        </div>
      ) : openKey ? (
        <Detail
          issue={current}
          onSetup={
            onSetup
              ? (to) => {
                  close();
                  onSetup(to);
                }
              : undefined
          }
          onBack={() => setOpenKey(null)}
          onAction={(x) => {
            close();
            onAction(x);
          }}
        />
      ) : (
        <>
          {cats.length > 1 && (
            <div className={css.cats}>
              <button
                type="button"
                className={css.cat}
                aria-pressed={cat == null}
                data-testid="warn-cat"
                onClick={() => setCat(null)}
              >
                {T('Alle')} {issues.length}
              </button>
              {cats.map(([c, name]) => (
                <button
                  type="button"
                  key={c}
                  className={css.cat}
                  aria-pressed={cat === c}
                  data-testid="warn-cat"
                  onClick={() => setCat(c)}
                >
                  {T(name)} {issues.filter((x) => x.cat === c).length}
                </button>
              ))}
            </div>
          )}
          <div className={css.list} ref={listRef}>
            {shown.length === 0 ? (
              <p className={css.none}>{T('Ingen varsler her nå.')}</p>
            ) : (
              shown.map((x) => (
                <button
                  type="button"
                  key={x.key}
                  data-key={x.key}
                  className={css.row}
                  data-testid="warn-row"
                  onClick={() => {
                    back.current = x.key;
                    scrollTop.current = listRef.current?.scrollTop ?? 0;
                    setOpenKey(x.key);
                  }}
                >
                  <SevIcon sev={x.sev} />
                  <span className={css.rowText}>
                    <span className={css.rowTitle}>{x.text}</span>
                    <span className={css.rowMeta}>
                      <span className={css.srOnly}>{x.sev === 'd' ? T('Brudd') : T('Advarsel')}: </span>
                      {x.who}
                      {x.when ? ` · ${x.when}` : ''}
                    </span>
                  </span>
                  <i className={css.chevron}>
                    <Icon name="chevronRight" size={14} />
                  </i>
                </button>
              ))
            )}
          </div>
          <div className={css.foot}>
            <span className={css.footCount} data-testid="warn-count">
              {catName
                ? T('{n} i {cat}', { n: count(shown.length), cat: T(catName).toLowerCase() })
                : T('{n} i dette utvalget', { n: count(shown.length) })}
            </span>
            {shown.length > 0 && (
              <button
                type="button"
                className={css.link}
                data-testid="warn-review"
                onClick={() => {
                  close();
                  onReview(
                    shown.map((x) => x.key),
                    cat,
                  );
                }}
              >
                {T('Gjennomgå')} <Icon name="arrowRight" size={18} />
              </button>
            )}
          </div>
        </>
      )}
    </>
  );
}

/** One issue: the sentence, what it was measured against, and the one place it is fixed. */
function Detail({
  issue,
  onBack,
  onAction,
  onSetup,
}: {
  issue: Issue | undefined;
  onBack: () => void;
  onAction: (x: Issue) => void;
  onSetup?: ((to: string) => void) | undefined;
}) {
  const head = useRef<HTMLButtonElement | null>(null);
  useLayoutEffect(() => head.current?.focus(), []);
  const backBtn = (
    <button type="button" className={css.back} ref={head} onClick={onBack} data-testid="warn-back">
      <Icon name="chevronLeft" size={18} /> {T('Alle varsler')}
    </button>
  );
  // the plan changed while the popover was open and the entry is gone (WP-15)
  if (!issue)
    return (
      <div className={css.detail} data-testid="warn-detail">
        {backBtn}
        <p className={css.none}>{T('Varselet er løst eller endret siden listen ble åpnet.')}</p>
      </div>
    );
  return (
    <>
      <div className={css.detail} data-testid="warn-detail">
        {backBtn}
        <h3 className={css.heading}>{issue.heading}</h3>
        <p className={css.description}>{issue.description}</p>
        <dl className={css.evidence} data-testid="warn-facts">
          {issue.evidence.map((f) => (
            <div className={css.fact} key={f.label}>
              <dt>{f.label}</dt>
              <dd>{f.value}</dd>
            </div>
          ))}
        </dl>
        <div className={css.rule}>
          <strong>{issue.source}</strong>
          <p>{issue.note}</p>
        </div>
        {issue.setup && onSetup && (
          <button
            type="button"
            className={css.link}
            data-testid="warn-setup"
            onClick={() => onSetup((issue.setup as { to: string }).to)}
          >
            {issue.setup.label} <Icon name="arrowRight" size={18} />
          </button>
        )}
      </div>
      <div className={css.foot}>
        <button
          type="button"
          className={css.primary}
          data-testid="warn-action"
          onClick={() => onAction(issue)}
        >
          {issue.actionLabel} <Icon name="arrowRight" size={18} />
        </button>
      </div>
    </>
  );
}
